import type { Theme } from "@mui/material";
import { RN_CATEGORY_ID } from "./cumulativeScoring.utils";
import { WEEKEND_REC_IDS } from "./planOutcome.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import type { PlanItem } from "./recommendations.constants";

// Sign-aware copy selection for a lever's change. Every number in the plan math is SIGNED (an add
// is positive, a cut negative), but the copy carries the direction itself ("−", "Saves", "Cut"), so
// the values handed to `t()` are always magnitudes. One place decides which key + magnitude a
// signed value maps to, so the slider card, the Action Plan row and the summary can't disagree.

// An i18n key plus the interpolation values it needs.
export type CopySpec = { key: string; values: Record<string, number> };

// How a points change should be colored: gained, lost, or a cut that keeps every point.
export type PointsTone = "gain" | "loss" | "kept" | "none";

// "+0.123 HPPD" / "−0.123 HPPD". The caller formats the magnitude, so each surface keeps its own
// precision rule.
export const hppdChangeCopy = (value: number): { key: string; magnitude: number } =>
	value < 0 ? { key: "pbj_model.recommendations.impact_hppd_cut", magnitude: -value } : { key: "pbj_model.recommendations.impact_hppd", magnitude: value };

// Points are shown to one decimal: band points are whole, but the CMS rescale (a measure N/A) makes
// them fractional (10 × 380/350 = 10.857…), and float residue on the 380 scale can leave −1e-15.
const POINTS_DISPLAY_FACTOR = 10;
export const roundPointsForDisplay = (points: number): number => {
	const rounded = Math.round(points * POINTS_DISPLAY_FACTOR) / POINTS_DISPLAY_FACTOR;
	// `Math.round(-0.04)` is -0; normalize so the sign checks and the printed figure agree.
	return rounded === 0 ? 0 : rounded;
};

// "+N pts" for a gain, "−N pts" for a loss, "No points lost" for a cut that stays in its band. An
// add that earns nothing keeps the existing "+0 pts". The sign is read from the ROUNDED figure, so a
// float residue never prints as "−0 pts"; `count` is that rounded magnitude so plurals resolve.
export const pointsChangeCopy = (points: number, isCut: boolean): CopySpec & { tone: PointsTone } => {
	const rounded = roundPointsForDisplay(points);
	if (rounded > 0) return { key: "pbj_model.recommendations.impact_points", values: { count: rounded }, tone: "gain" };
	if (rounded < 0) return { key: "pbj_model.recommendations.points_lost", values: { count: -rounded }, tone: "loss" };
	if (isCut) return { key: "pbj_model.recommendations.no_points_lost", values: {}, tone: "kept" };
	return { key: "pbj_model.recommendations.impact_points", values: { count: 0 }, tone: "none" };
};

// The ink a points figure wears: the lever's own accent for a gain (and for the neutral "+0 pts"), the
// error red for points lost, and the savings green for a cut that keeps every point. One mapping, so the
// lever card and its Action Plan row can't color the same figure differently.
export const pointsToneInk = (theme: Theme, tone: PointsTone, accent: string): string => {
	switch (tone) {
		case "gain":
		case "none":
			return accent;
		case "loss":
			return theme.palette.error.main;
		case "kept":
			return theme.palette.success.main;
	}
};

// "$X total" for a cost, "Saves $X" for savings, in whole dollars. Direction comes from the change
// itself (`isCut`, the spend's sign), not the dollars: a cut too small to commit a whole hour prices at
// $0 and must still read "Saves $0", not "$0 total". `magnitude` is already rounded to whole dollars;
// `value` is that magnitude formatted for the copy's `{{value}}`.
export const moneyChangeCopy = (dollars: number, isCut: boolean): { key: string; magnitude: number; value: string; isSavings: boolean } => {
	const magnitude = Math.round(Math.abs(dollars));
	const value = magnitude.toLocaleString(undefined, { maximumFractionDigits: 0 });
	return isCut
		? { key: "pbj_model.recommendations.savings_total", magnitude, value, isSavings: true }
		: { key: "pbj_model.recommendations.cost_total", magnitude, value, isSavings: false };
};

// The Action Plan's headline for one item. Adds keep the lever's own copy ("Schedule N additional
// hours per week"); cuts share one wording, with a weekend variant because the add copy names
// weekend hours too. `hours` is per week, as a magnitude. Direction comes from `spend`, which keeps
// its sign even when a tiny change rounds to 0 whole hours.
export const actionCopyKey = (item: Pick<PlanItem, "id" | "spend" | "actionKey" | "actionCatchupKey">, periodMode: PeriodMode): string => {
	const isCatchup = periodMode === "qtd";
	if (item.spend >= 0) return isCatchup ? item.actionCatchupKey : item.actionKey;
	const stem = WEEKEND_REC_IDS.has(item.id) ? "pbj_model.recommendations.cut_action_weekend" : "pbj_model.recommendations.cut_action";
	return isCatchup ? `${stem}_catchup` : stem;
};

// Which "catch-up not feasible" reason applies when an item has no catch-up rate. No applicable
// days left (a finished quarter, or no weekend left for a weekend lever) is the reason for adds AND
// cuts; only a cut with days left can fail because its catch-up rate would pass the cut limit.
// `signedChange` is any of the item's signed figures (its spend) — only the sign is read.
export const catchupInfeasibleKey = (signedChange: number, applicableDaysRemaining: number): string =>
	signedChange < 0 && applicableDaysRemaining > 0 ? "pbj_model.action_plan.catchup_infeasible_cut" : "pbj_model.action_plan.catchup_infeasible";

// A lever touching the RN HPPD category may only be cut while every day keeps an RN on duty, so
// cutting it carries the federal-floor warning.
export const isRnLever = (impactedIds: readonly string[]): boolean => impactedIds.includes(RN_CATEGORY_ID);

// Widened to plain strings so a backend step's `actionId` (typed string) can be looked up directly.
const WEEKEND_ACTION_IDS: ReadonlySet<string> = WEEKEND_REC_IDS;

// Compatibility analytics for cut steps in the returned plan. Additions-only suggestions make both
// values zero; retaining the calculation preserves the event schema for older responses.
export const cutStepsSummary = (
	steps: ReadonlyArray<{ actionId: string; spend: number; pricedSpend: number }>,
	census: number,
	quarterDays: number,
	quarterWeekendDays: number,
): { count: number; savings: number } => {
	let count = 0;
	let savings = 0;
	for (const step of steps) {
		if (step.spend >= 0) continue;
		count += 1;
		const days = WEEKEND_ACTION_IDS.has(step.actionId) ? quarterWeekendDays : quarterDays;
		savings += -step.pricedSpend * census * days;
	}
	return { count, savings };
};
